import { useQuery, UseQueryResult } from "@tanstack/react-query";
import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export type SystemIdLocation = "EMAIL_SUBJECT" | "EMAIL_BODY" | "FILE_NAME";

/** Paired with systemIdLocation; empty array means match vendor by system ID only. */
export interface ProfileCostImportVendorKeyword {
  vendorKeyword: string;
  vendorConnectionProfileId: string;
}

export interface ProfileCostImportConfig {
  id: number;
  profileId: string;
  rateHeader: string;
  effectiveDateHeader: string;
  mccmncHeader?: string;
  mccHeader?: string;
  mncHeader?: string;
  countryHeader?: string;
  operatorHeader?: string;
  systemIdLocation: SystemIdLocation;
  matchFileName?: string;
  vendorKeywords?: ProfileCostImportVendorKeyword[];
  requireConfirmation: boolean;
  overrideExisting: boolean;
  rateCount: number;
  enabled: boolean;
  isDeleted: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdDate: string;
  timestamp?: string;
  version: number;
}

export interface ProfileCostImportConfigParams extends Params<ProfileCostImportConfig> {}

export const SYSTEM_ID_LOCATION_OPTIONS = [
  { label: "Email Subject", value: "EMAIL_SUBJECT" },
  { label: "Email Body", value: "EMAIL_BODY" },
  { label: "File Name", value: "FILE_NAME" },
];

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || fallback;

export async function fetchProfileCostImportConfigs(
  params: ProfileCostImportConfigParams,
): Promise<IPageable<ProfileCostImportConfig>> {
  try {
    const response = await axiosInstance.get("/profile-cost-import-config", {
      params,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while fetching profile cost import configurations.",
      ),
    );
  }
}

export async function fetchAllProfileCostImportConfigs(): Promise<
  ProfileCostImportConfig[]
> {
  try {
    const response = await axiosInstance.get("/profile-cost-import-config/all");
    return response.data;
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while fetching all profile cost import configurations.",
      ),
    );
  }
}

export async function fetchProfileCostImportConfigById(
  id: number | string,
): Promise<ProfileCostImportConfig> {
  try {
    const response = await axiosInstance.get(
      `/profile-cost-import-config/${id}`,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while fetching the profile cost import configuration.",
      ),
    );
  }
}

export async function fetchProfileCostImportConfigByProfile(
  profileId: string,
): Promise<ProfileCostImportConfig | null> {
  try {
    const response = await axiosInstance.get(
      `/profile-cost-import-config/by-profile/${profileId}`,
    );
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while checking profile cost import configuration.",
      ),
    );
  }
}

export async function createProfileCostImportConfig(
  data: Partial<ProfileCostImportConfig>,
): Promise<ProfileCostImportConfig> {
  try {
    const response = await axiosInstance.post(
      "/profile-cost-import-config",
      data,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while creating the profile cost import configuration.",
      ),
    );
  }
}

export async function updateProfileCostImportConfig(
  id: number | string,
  data: Partial<ProfileCostImportConfig>,
): Promise<ProfileCostImportConfig> {
  try {
    const response = await axiosInstance.put(
      `/profile-cost-import-config/${id}`,
      data,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while updating the profile cost import configuration.",
      ),
    );
  }
}

export async function deleteProfileCostImportConfig(
  id: number | string,
): Promise<void> {
  try {
    await axiosInstance.delete(`/profile-cost-import-config/${id}`);
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while deleting the profile cost import configuration.",
      ),
    );
  }
}

export async function exportProfileCostImportConfigsCsv(
  params: ProfileCostImportConfigParams = {},
): Promise<void> {
  try {
    const response = await axiosInstance.get(
      "/profile-cost-import-config/csv",
      {
        params,
        responseType: "blob",
      },
    );
    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, "profile-cost-import-config.csv");
  } catch (error: any) {
    throw new Error(
      getErrorMessage(
        error,
        "An error occurred while exporting profile cost import configurations.",
      ),
    );
  }
}

export const useProfileCostImportConfigsQuery = (
  params: ProfileCostImportConfigParams,
): UseQueryResult<IPageable<ProfileCostImportConfig>, Error> => {
  const queryKey = ["profileCostImportConfig", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchProfileCostImportConfigs(params),
  });
};

export interface ProfileOption {
  label: string;
  value: string;
}

interface ProfileOptionsResult {
  options: ProfileOption[];
  profileMap: Record<string, string>;
}

export async function fetchProfileOptions(): Promise<ProfileOptionsResult> {
  try {
    const response = await axiosInstance.get("/profiles/all");
    type MinimalProfile = {
      id: string;
      name: string;
      isDeleted?: boolean;
      rateUpdatesEmail?: string;
    };
    const profiles = (
      Array.isArray(response.data)
        ? response.data
        : response.data?.content || []
    ) as MinimalProfile[];
    const filteredProfiles = profiles.filter(
      (profile) =>
        profile?.isDeleted === false || profile?.isDeleted === undefined,
    );
    const options: ProfileOption[] = filteredProfiles.map((profile) => ({
      label: profile.rateUpdatesEmail
        ? `${profile.name} (${profile.rateUpdatesEmail})`
        : profile.name,
      value: profile.id,
    }));
    const profileMap = options.reduce<Record<string, string>>((acc, option) => {
      acc[String(option.value)] = option.label;
      return acc;
    }, {});
    return { options, profileMap };
  } catch (error: any) {
    throw new Error(
      getErrorMessage(error, "An error occurred while fetching profiles."),
    );
  }
}

export const useProfileOptionsQuery = (): UseQueryResult<
  ProfileOptionsResult,
  Error
> => {
  return useQuery({
    queryKey: ["profiles", "all"],
    queryFn: fetchProfileOptions,
  });
};
